import config from 'config';
import { action, computed, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    deepStringFind,
    IMultiSelectOption,
    inject,
    Modal,
    ModalHandle,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowsCollapse } from '@react-icons/all-files/bs/BsArrowsCollapse';
import { BsArrowsExpand } from '@react-icons/all-files/bs/BsArrowsExpand';
import { BsGearFill } from '@react-icons/all-files/bs/BsGearFill';
import { BsSlashSquare } from '@react-icons/all-files/bs/BsSlashSquare';
import { BsTrash } from '@react-icons/all-files/bs/BsTrash';
import { BsTextareaT } from '@react-icons/all-files/bs/BsTextareaT';
import { BsTextarea } from '@react-icons/all-files/bs/BsTextarea';

import {
    ButtonDropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    Input,
    UncontrolledButtonDropdown,
} from 'reactstrap';
import { AccountStore, IUserColumnPreset } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { DisplayListViewModel } from '../DisplayFilters/DisplayListViewModel';
import { CreateColumnPresetModal } from './CreateColumnPresetModal';
import { displayConfigCells } from './DisplayCells_Config';
import { displayTelemetryCells } from './DisplayCells_Telemetry';

export enum DisplayViewTypes {
    CardsSmall,
    CardsLarge,
    TableSmall,
    TableLarge,
    MapView,
    LiveChartsView,
    Aggregate,
}

export interface IDisplaysTableSettingsProps {
    readonly vm: DisplayListViewModel;
    readonly className?: string;
    readonly link?: boolean;
}

@autoinject
@observer
export class DisplaysTableSettings extends React.Component<IDisplaysTableSettingsProps> {
    public render() {
        const presetColumns = this.accountStore.storage.presetColumns ?? [];
        const presetStickyColumns = this.accountStore.storage.presetStickyColumns ?? [];

        return (
            <>
                <ButtonDropdown
                    className={this.props.className}
                    direction="down"
                    isOpen={this.dropdownOpen}
                    toggle={this.onDropdownToggle}
                >
                    <DropdownToggle caret color={this.props.link ? 'link' : undefined} className="ps-0">
                        <Tooltip message="Table Settings">
                            <BsGearFill />
                        </Tooltip>
                    </DropdownToggle>

                    <DropdownMenu end className="py-0">
                        <DropdownItem header className="fw-bold">
                            Toggles
                        </DropdownItem>
                        <DropdownItem tag="a" className="text-header fw-bold mt-2" onClick={this.clearAllSticky}>
                            <BsSlashSquare style={{ fontSize: 19, marginRight: 7 }} className="" />
                            Clear Stickies
                        </DropdownItem>

                        <DropdownItem tag="a" className="text-header fw-bold mb-2" onClick={this.toggleHeaderWrap}>
                            {!this.accountStore.storage.wrappedHeaders && (
                                <BsArrowsCollapse style={{ fontSize: 20, marginRight: 3 }} className="" />
                            )}
                            {this.accountStore.storage.wrappedHeaders && (
                                <BsArrowsExpand style={{ fontSize: 20, marginRight: 3 }} className="" />
                            )}
                            {this.accountStore.storage.wrappedHeaders ? ' Un-Wrap Headers' : ' Wrap Headers'}
                        </DropdownItem>

                        <DropdownItem tag="a" className="text-header fw-bold mb-2" onClick={this.toggleHiddenUnits}>
                            {!this.accountStore.storage.hideUnits && (
                                <BsTextarea style={{ fontSize: 20, marginRight: 3 }} className="" />
                            )}
                            {this.accountStore.storage.hideUnits && (
                                <BsTextareaT style={{ fontSize: 20, marginRight: 3 }} className="" />
                            )}
                            {this.accountStore.storage.hideUnits ? ' Show Units' : ' Hide Units'}
                        </DropdownItem>

                        <DropdownItem header className="fw-bold">
                            Presets
                        </DropdownItem>

                        <DropdownItem divider className="m-0" />
                        <Input
                            type="text"
                            placeholder="Find Preset..."
                            value={this.findString}
                            onChange={this.onFindChange}
                        />
                        <DropdownItem
                            className="btn btn-primary text-center dropdown-header"
                            onClick={this.openCreatePresetModal}
                        >
                            Create New Preset
                        </DropdownItem>

                        {!!presetColumns?.length && this.generatePresetRows(presetColumns)}
                        <DropdownItem
                            className="btn btn-primary text-center dropdown-header"
                            onClick={this.openCreateStickyPresetModal}
                        >
                            Create New Sticky Preset
                        </DropdownItem>
                        {!!presetStickyColumns?.length && this.generatePresetRows(presetStickyColumns, 'sticky')}
                    </DropdownMenu>
                </ButtonDropdown>

                <Modal
                    className="modal-sm"
                    title="Confirm Deletion"
                    modalHandle={this.confirmModalHandle}
                    btnClass="w-50"
                    footerClass="d-flex justify-content-center"
                >
                    <div className="text-center">Delete Preset?</div>
                </Modal>

                <CreateColumnPresetModal modalHandle={this.createPresetModalHandle} />
                <CreateColumnPresetModal modalHandle={this.createPresetStickyModalHandle} type="sticky" />
            </>
        );
    }

    /**
     * Generate template for preset rows
     * @param presetColumns
     * @param type - Enum used to determine which click method is chosen
     * @private
     */
    private generatePresetRows(presetColumns: IUserColumnPreset[], type?: 'normal' | 'sticky') {
        const onClickMethod = type === 'sticky' ? this.onStickyPresetChange : this.onPresetChange;
        const isSticky = type == 'sticky';
        return presetColumns.map((p, i) => (
            <DropdownItem
                key={i}
                className={
                    isSticky
                        ? this.getIsStickyPresetSelected(p)
                            ? 'd-flex bg-blue'
                            : 'd-flex'
                        : this.getIsColumnPresetSelected(p)
                            ? 'd-flex bg-blue'
                            : 'd-flex'
                }
                onClick={() => onClickMethod(p)}
            >
                {p.name}
                <span className="ms-auto">&nbsp; ({this.getColumnCount(p)})</span>

                <Tooltip message="Delete Preset">
                    <a className="btn btn-link py-0" onClick={() => this.deletePresetAsync(p, type)}>
                        <BsTrash className="text-danger" />
                    </a>
                </Tooltip>
            </DropdownItem>
        ));
    }

    @bind
    @action
    private getIsColumnPresetSelected(preset: IUserColumnPreset) {
        if (this.accountStore.storage.activeColumnPreset == preset.name) return true;

        return false;
    }

    @bind
    @action
    private getIsStickyPresetSelected(preset: IUserColumnPreset) {
        if (this.accountStore.storage.activeStickyPreset == preset.name) return true;

        return false;
    }

    @bind
    @action
    private onDropdownToggle() {
        this.dropdownOpen = !this.dropdownOpen;
    }

    @bind
    @action
    private clearAllSticky() {
        this.accountStore.storage.activeStickyPreset = '';
        this.props.vm.clearAllStickyColumns();
    }

    @bind
    @action
    private toggleHeaderWrap() {
        this.accountStore.storage.wrappedHeaders = !this.accountStore.storage.wrappedHeaders;
    }

    @bind
    @action
    private toggleHiddenUnits() {
        this.accountStore.storage.hideUnits = !this.accountStore.storage.hideUnits;
    }

    @bind
    @action
    private async deletePresetAsync(preset: IUserColumnPreset, type: 'sticky' | 'normal' = 'normal') {
        const presetType = type === 'sticky' ? 'presetStickyColumns' : 'presetColumns';
        if (await this.confirmModalHandle.confirm()) {
            const index = this.accountStore.storage[presetType].findIndex(c => c.name == preset.name);
            this.accountStore.storage[presetType].splice(index, 1);
        }
    }

    @bind
    @action
    private onPresetChange(preset: IUserColumnPreset, type: 'sticky' | 'normal' = 'normal') {
        if (!this.confirmModalHandle.isShowing) {
            this.props.vm.clearAllColumns();

            const filteredColumns = this.columnOptions.filter(c => {
                return preset.columns.some(cl => cl.option === c.option);
            });
            const filteredConfigColumns = this.configColumnOptions.filter(c => {
                return preset?.configColumns?.some(cl => cl.option === c.option);
            });
            this.accountStore.storage.activeColumnPreset = preset.name;
            this.props.vm.onColumnChange(filteredColumns);
            this.props.vm.onConfigColumnChange(filteredConfigColumns);
        }
    }

    /**
     * When a sticky preset is changed, columns need to be updated to still retain existing selected columns and
     * config columns, but also check to make sure that the missing sticky columns are added to the selected columns.
     * This is accomplished by grabbing the current selected columns and config columns from the accountStore,
     * mapping them to flat arrays with only names (to improve filter speed), checking to see which columns are missing
     * from the user's selected columns, then using the available columnOptions to grab the correct column object
     * for the missing columns. The columns are then cleared and the onColumnChange and onConfigColumnChange methods
     * are called to update the columns.
     * @param preset<IUserColumnPreset> The preset object reused from the preset modal. Unlike with the normal presets,
     * the sticky preset object does not contain the columns and config columns, but rather the column names, as this
     * is how sticky columns are set, instead of a normal column object.
     * @private
     */
    @bind
    @action
    private onStickyPresetChange(preset: IUserColumnPreset) {
        if (this.confirmModalHandle.isShowing) return;
        const { displayTableColumns, displayTableConfigColumns } = this.accountStore.storage;
        this.accountStore.storage.activeStickyPreset = preset.name;
        let currentlySelectedColumns = toJS(displayTableColumns);
        let currentlySelectedConfigColumns = toJS(displayTableConfigColumns);
        const flattenedDisplayTableColumns = currentlySelectedColumns.map(c => c.option);
        const flattenedDisplayTableConfigColumns = currentlySelectedConfigColumns.map(c => c.option);
        const unknownColumns: string[] = [];
        const stickyColumns: string[] = toJS(preset).columns.map(c => {
            if (
                flattenedDisplayTableColumns.indexOf(c.option) === -1 &&
                flattenedDisplayTableConfigColumns.indexOf(c.option) === -1
            )
                unknownColumns.push(c.option.toString());
            return c.option.toString();
        });
        const missingColumns = this.columnOptions.filter(c => unknownColumns.indexOf(c.option.toString()) !== -1);
        const missingConfigColumns = this.configColumnOptions.filter(
            c => unknownColumns.indexOf(c.option.toString()) !== -1
        );
        currentlySelectedColumns = [...currentlySelectedColumns, ...missingColumns];
        currentlySelectedConfigColumns = [...currentlySelectedConfigColumns, ...missingConfigColumns];
        this.props.vm.clearAllColumns();
        this.props.vm.onColumnChange(currentlySelectedColumns);
        this.props.vm.onConfigColumnChange(currentlySelectedConfigColumns);
        this.props.vm.bulkUpdateStickyColumns(stickyColumns);
    }

    @computed
    private get columnOptions() {
        return displayTelemetryCells.map(c => {
            return { option: c.id, group: c.group };
        });
    }

    @computed
    private get configColumnOptions() {
        return displayConfigCells.map(c => {
            return { option: c.id, group: c.group };
        });
    }

    @bind
    private openCreatePresetModal(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.createPresetModalHandle.open();
    }

    @bind
    private openCreateStickyPresetModal(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();
        this.createPresetStickyModalHandle.open();
    }

    @bind
    @action
    private onFindChange(event: React.FormEvent<HTMLInputElement>) {
        const target = event.target as HTMLInputElement;
        this.findString = target.value;
    }

    @bind
    private getColumnCount(preset: IUserColumnPreset) {
        const configCount = preset.configColumns?.length ?? 0;
        const columnCount = preset.columns?.length ?? 0;
        return configCount + columnCount;
    }

    @observable
    private findString: string = '';
    @observable
    private dropdownOpen: boolean = false;
    @inject
    private accountStore!: AccountStore;
    private createPresetModalHandle = new ModalHandle();
    private createPresetStickyModalHandle = new ModalHandle();
    private confirmModalHandle = new ModalHandle();
}
